import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from '@noor/lib';
import { isDeliveryForbiddenError } from '@/shared/lib/delivery-errors';
import { merchantChatApi, merchantChatKeys } from '@/entities/merchant-chat';

/**
 * Send a message into a thread. No optimistic echo — the old composer waited
 * for the round trip and then refetched the thread and the list, and the send
 * button's pending state covers the wait the same way here.
 */
export function useSendMerchantMessage() {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  return useMutation({
    // The error toast below replaces the global MutationCache one.
    meta: { suppressErrorToast: true },
    mutationFn: ({
      chatId,
      message,
      files,
    }: {
      chatId: number;
      message: string;
      files: File[];
    }) => merchantChatApi.send(chatId, { message, files }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: merchantChatKeys.all });
    },
    onError: (error) => {
      if (isDeliveryForbiddenError(error)) return;
      toast.error(t('chat.toast.message_error'));
    },
  });
}
